import { FormikProps, withFormik } from 'formik'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Yup from 'yup'

import { Button, Spacer } from '@syconium/little-miss-figgy'

import { useAuthentication } from '../../../../app/_providers/AuthenticationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { CtaButton } from '../../../../brunswick/components/CtaButton'
import { CartContainer } from '../../../../brunswick/containers/cart'
import focusInvalidFormField from '../../../../lib/utils/focusInvalidFormField'
import { EMAIL_FORM_NAME, PASSWORD_FORM_NAME } from '../constants'
import { FormInput } from '../formInput'
import { ButtonWrapper } from '../styles'

import { IInputRefs, LoginProps } from './IProps'
import { Error, OptionsButton, SpaceBetween, TextContainer } from './styles'

const ERROR_MESSAGE_ID = 'magnolia-login-form-input-error-id'

const PasswordOptions: React.FC<{
  displayPasswordReset: (arg0: boolean) => void
  setShowPassword: React.Dispatch<React.SetStateAction<boolean>>
  showPassword: boolean
}> = ({ displayPasswordReset, showPassword, setShowPassword }) => {
  const {
    magnolia: { general: strings },
  } = useTranslation()
  return (
    <SpaceBetween>
      <OptionsButton onClick={() => displayPasswordReset(true)} type='button'>
        <TextContainer>{strings.forgotYourPassword}</TextContainer>
      </OptionsButton>
      <OptionsButton onClick={() => setShowPassword(!showPassword)} type='button'>
        <TextContainer>{showPassword ? strings.hidePassword : strings.showPassword}</TextContainer>
      </OptionsButton>
    </SpaceBetween>
  )
}

const LoginForm: React.FC<
  FormikProps<LoginProps> & {
    checkoutUrl?: string
    redirectUrl?: string
    displayPasswordReset: (arg0: boolean) => void
  }
> = props => {
  const { checkoutUrl, redirectUrl, displayPasswordReset, ...formikProps } = props

  const {
    setFieldValue,
    values: { email, password },
    handleSubmit,
    validateForm,
  } = formikProps

  const { isLoggedIn, logIn, loginMutationErrors, loginMutationStatus, logOut, account } =
    useAuthentication()
  const onLogOutClick = useCallback(() => {
    logOut()
  }, [logOut])
  const { proceedToCheckout } = CartContainer.useContainer()
  const {
    magnolia: { general: strings },
  } = useTranslation()

  const emailInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
  const passwordInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
  const inputRefs: IInputRefs = useMemo(() => {
    return {
      [strings.emailLabel!]: {
        order: 0,
        ref: emailInputRef,
      },
      [strings.passwordLabel!]: {
        order: 1,
        ref: passwordInputRef,
      },
    }
  }, [strings.emailLabel, strings.passwordLabel])

  const [showPassword, setShowPassword] = useState<boolean>(false)

  const showLogout = isLoggedIn

  const onLoginFormSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      handleSubmit()
      const formValidationResult = await validateForm()
      const inputEmail = email || account?.email

      if (
        !!Object.entries(formValidationResult).length ||
        !inputEmail ||
        !inputEmail.includes('@')
      ) {
        return
      }

      logIn({
        email: inputEmail,
        password,
        destination: checkoutUrl ? proceedToCheckout : redirectUrl,
      })
    },
    [
      account?.email,
      checkoutUrl,
      email,
      handleSubmit,
      logIn,
      password,
      proceedToCheckout,
      redirectUrl,
      validateForm,
    ]
  )

  const loginError = loginMutationErrors.length > 0

  // set field focused to announce validation error according to accessibility guidelines
  useEffect(() => {
    focusInvalidFormField(formikProps.errors, formikProps.touched, inputRefs)
  }, [inputRefs, formikProps.errors, formikProps.touched])

  // NOTE: set email from localstorage if it exists
  useEffect(() => {
    if (!email && account?.email) {
      setFieldValue(EMAIL_FORM_NAME, account?.email)
    }
  }, [account?.email, email, setFieldValue])

  return (
    <>
      {showLogout ? (
        <ButtonWrapper>
          <Button block onClick={onLogOutClick} variant='filled'>
            {strings.logout}
          </Button>
        </ButtonWrapper>
      ) : (
        <form onSubmit={onLoginFormSubmit}>
          <FormInput
            {...props}
            errorMessageId={`${ERROR_MESSAGE_ID}_0`}
            name={EMAIL_FORM_NAME}
            placeholder={strings.email}
            ref={inputRefs[strings.emailLabel!]?.ref}
            required
            type='email'
          />
          <Spacer height={12} />
          <FormInput
            {...props}
            errorMessageId={`${ERROR_MESSAGE_ID}_1`}
            name={PASSWORD_FORM_NAME}
            placeholder={strings.password}
            ref={inputRefs[strings.passwordLabel!]?.ref}
            required
            type={showPassword ? 'text' : 'password'}
          />
          <PasswordOptions {...{ displayPasswordReset, setShowPassword, showPassword }} />
          {loginError && (
            <Error data-testid='submission-error'>{strings.incorrectCredentials}</Error>
          )}
          <ButtonWrapper>
            <CtaButton block isProcessing={loginMutationStatus === 'pending'} type='submit'>
              {strings.logIn}
            </CtaButton>
          </ButtonWrapper>
        </form>
      )}
    </>
  )
}

type IProps = { checkoutUrl?: string; displayPasswordReset: (arg0: boolean) => void }

const LoginWithFormik = (props: IProps) => {
  const translations = useTranslation()
  const LoginFormWithFormik = withFormik<IProps, LoginProps>({
    mapPropsToValues: () => ({ email: '', password: '' }),
    validationSchema: () => {
      return Yup.object().shape({
        email: Yup.string().email().required(translations.magnolia.general.emailWarning),
        password: Yup.string()
          .min(1)
          .max(40, translations.magnolia.general.maxPasswordLengthValidationMessage)
          .required(translations.magnolia.general.passwordWarning),
      })
    },
    handleSubmit: () => {},
  })(LoginForm)
  return <LoginFormWithFormik {...props} />
}

export default LoginWithFormik
